/**
 * Offer Service Layer
 * Handles API calls for offers
 */

import type { Locale } from '@/types/pokemon'
import type { Offer, OfferType } from '../types/offer'
import { getApiUrl } from '@/lib/utils'
import type { StandardCondition } from '@/types/inventory'

const API_URL = getApiUrl()

function getFetchOptions(method: string, body?: unknown): RequestInit {
  const options: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  }
  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }
  return options
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Request failed: ${response.status}`)
  }
  return data as T
}

function deserializeOffer(offer: Offer): Offer {
  if (offer.createdAt) offer.createdAt = new Date(offer.createdAt)
  if (offer.updatedAt) offer.updatedAt = new Date(offer.updatedAt)
  offer.sellPositions?.forEach((p) => {
    if (p.createdAt) p.createdAt = new Date(p.createdAt)
    if (p.updatedAt) p.updatedAt = new Date(p.updatedAt)
  })
  offer.buyPositions?.forEach((p) => {
    if (p.createdAt) p.createdAt = new Date(p.createdAt)
    if (p.updatedAt) p.updatedAt = new Date(p.updatedAt)
  })
  return offer
}

/**
 * Get all offers for the logged-in user.
 */
export async function getOffers(type?: OfferType): Promise<Offer[]> {
  const url = type ? `${API_URL}/api/offers?type=${type}` : `${API_URL}/api/offers`
  const response = await fetch(url, getFetchOptions('GET'))
  const offers = await handleResponse<Offer[]>(response)
  return offers.map(deserializeOffer)
}

export type CreateOfferInput = {
  type: OfferType
  title?: string | null
  status?: string
}

/**
 * Create a new offer.
 */
export async function createOffer(input: CreateOfferInput): Promise<Offer> {
  const response = await fetch(`${API_URL}/api/offers`, getFetchOptions('POST', input))
  const offer = await handleResponse<Offer>(response)
  return deserializeOffer(offer)
}

/**
 * Get a single offer by ID.
 */
export async function getOffer(id: string): Promise<Offer> {
  const response = await fetch(`${API_URL}/api/offers/${id}`, getFetchOptions('GET'))
  const offer = await handleResponse<Offer>(response)
  return deserializeOffer(offer)
}

export type UpdateOfferInput = {
  title?: string | null
  status?: string
}

/**
 * Update an offer.
 */
export async function updateOffer(id: string, input: UpdateOfferInput): Promise<Offer> {
  const response = await fetch(`${API_URL}/api/offers/${id}`, getFetchOptions('PATCH', input))
  const offer = await handleResponse<Offer>(response)
  return deserializeOffer(offer)
}

/**
 * Add a sell position to an offer.
 */
export async function addSellPosition(offerId: string, inventoryCardId: string, price: number): Promise<Offer> {
  const response = await fetch(
    `${API_URL}/api/offers/${offerId}/sell-positions`,
    getFetchOptions('POST', {
      inventoryCardId,
      price,
    }),
  )
  await handleResponse<{ id: string }>(response)
  return getOffer(offerId)
}

/**
 * Update a sell position's price.
 */
export async function updateSellPosition(positionId: string, price: number): Promise<void> {
  await fetch(`${API_URL}/api/offers/sell-positions/${positionId}`, getFetchOptions('PATCH', { price }))
}

/**
 * Delete a sell position.
 */
export async function deleteSellPosition(positionId: string): Promise<void> {
  await fetch(`${API_URL}/api/offers/sell-positions/${positionId}`, getFetchOptions('DELETE'))
}

/**
 * Add a buy position to an offer.
 */
export async function addBuyPosition(offerId: string, cardId: string, condition: StandardCondition | null, price: number | null, locale: Locale): Promise<Offer> {
  await fetch(
    `${API_URL}/api/offers/${offerId}/buy-positions`,
    getFetchOptions('POST', {
      cardId,
      condition,
      price,
      locale: locale,
    }),
  )
  return getOffer(offerId)
}

/**
 * Update a buy position.
 */
export async function updateBuyPosition(
  positionId: string,
  input: { cardId?: string; locale?: Locale; condition?: StandardCondition | null; price?: number | null },
): Promise<void> {
  await fetch(`${API_URL}/api/offers/buy-positions/${positionId}`, getFetchOptions('PATCH', input))
}

/**
 * Delete a buy position.
 */
export async function deleteBuyPosition(positionId: string): Promise<void> {
  await fetch(`${API_URL}/api/offers/buy-positions/${positionId}`, getFetchOptions('DELETE'))
}

/**
 * Fetches the offer PDF and opens it in a new tab for viewing or printing.
 */
export async function openOfferPdf(offerId: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/print/offers/${encodeURIComponent(offerId)}/pdf`, {
    method: 'GET',
    credentials: 'include',
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error((data as { error?: string }).error ?? `Failed to generate PDF (${response.status})`)
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  window.setTimeout(() => URL.revokeObjectURL(url), 300_000)
}
